<script setup>
import { onBeforeUnmount } from 'vue'
import FormSelectField from './FormSelectField.vue'
import ModalEngineDetails from './ModalEngineDetails.vue'

const props = defineProps({
    form: {
        type: Object,
        required: true,
    },
    errors: {
        type: Object,
        required: true,
    },
    brands: {
        type: Array,
        required: true,
    },
    models: {
        type: Array,
        required: true,
    },
    versions: {
        type: Array,
        required: true,
    },
    engines: {
        type: Array,
        default: () => [],
    },
    showEngines: {
        type: Boolean,
        default: true,
    },
    showEngineButtons: {
        type: Boolean,
        default: true,
    },
    showDetailedLink: {
        type: Boolean,
        default: true,
    },
    modalId: {
        type: String,
        default: 'modalEngineDetails',
    },
    typeField: {
        type: String,
        default: 'vehicle_type',
    },
    brandField: {
        type: String,
        default: 'vehicle_brand_id',
    },
    modelField: {
        type: String,
        default: 'vehicle_model_id',
    },
    versionField: {
        type: String,
        default: 'vehicle_version_id',
    },
    engineField: {
        type: String,
        default: 'vehicle_engine_id',
    },
    typeBsClass: {
        type: String,
        default: 'col-xl-6',
    },
    brandBsClass: {
        type: String,
        default: 'col-xl-6',
    },
    modelBsClass: {
        type: String,
        default: 'col-xl-6',
    },
    versionBsClass: {
        type: String,
        default: 'col-xl-6',
    },
    engineBsClass: {
        type: String,
        default: 'col-xl-12',
    },
})

const emit = defineEmits([
    'update:vehicleType',
    'update:brandId',
    'update:modelId',
    'update:versionId',
    'update:engineId',
])

const vehicleTypeOptions = [
    { text: 'Voiture', value: 'car' },
    { text: 'Agricole', value: 'agricultural' },
    { text: 'ATV', value: 'atv' },
    { text: 'Camion', value: 'truck' },
    { text: 'Jetski', value: 'jetski' },
    { text: 'Moto', value: 'moto' },
    { text: 'Reinitialiser configurateur', value: null },
]

function updateVehicleType(value) {
    props.form[props.typeField] = value
    emit('update:vehicleType', value)
}

function updateBrandId(value) {
    props.form[props.brandField] = value
    emit('update:brandId', value)
}

function updateModelId(value) {
    props.form[props.modelField] = value
    emit('update:modelId', value)
}

function updateVersionId(value) {
    props.form[props.versionField] = value
    emit('update:versionId', value)
}

function updateEngineId(value) {
    props.form[props.engineField] = value
    emit('update:engineId', value)
}

function showModalEngine() {
    const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById(props.modalId))
    modal.show()
}

onBeforeUnmount(() => {
    const modalElement = document.getElementById(props.modalId)
    if (modalElement) {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    }
})
</script>

<template>
    <form-select-field
        :name="typeField"
        label="Type vehicule"
        :label-hidden="false"
        @update:field="updateVehicleType"
        :errors="errors"
        :required="false"
        :data="form[typeField]"
        :bs-class="typeBsClass"
        :disabled="false"
        :placeholder="form[typeField] === null ? 'Selectionnez un type' : null"
        :liste-options="vehicleTypeOptions"
    />

    <form-select-field
        :name="brandField"
        label="Marques"
        :label-hidden="false"
        @update:field="updateBrandId"
        :errors="errors"
        :required="false"
        :data="form[brandField]"
        :bs-class="brandBsClass"
        :liste-options="brands"
        :placeholder="form[typeField] === null ? 'Selectionnez un type' : 'Toutes les marques'"
        :disabled="form[typeField] === null"
    />

    <form-select-field
        :name="modelField"
        label="Modeles"
        :label-hidden="false"
        @update:field="updateModelId"
        :errors="errors"
        :required="false"
        :data="form[modelField]"
        :bs-class="modelBsClass"
        :disabled="form[brandField] === null"
        :liste-options="models"
        :placeholder="form[brandField] === null ? 'Selectionnez une marque' : 'Tous les modeles'"
    />

    <form-select-field
        :name="versionField"
        label="Versions"
        :label-hidden="false"
        @update:field="updateVersionId"
        :errors="errors"
        :required="false"
        :data="form[versionField]"
        :bs-class="versionBsClass"
        :disabled="form[modelField] === null"
        :liste-options="versions"
        :placeholder="form[modelField] === null ? 'Selectionnez un modele' : 'Toutes les versions'"
    />

    <form-select-field
        v-if="showEngines"
        :name="engineField"
        label="Motorisation"
        :label-hidden="false"
        @update:field="updateEngineId"
        :errors="errors"
        :required="false"
        :data="form[engineField]"
        :bs-class="engineBsClass"
        :disabled="form[versionField] === null"
        :liste-options="engines"
        :placeholder="form[engineField] === null ? 'Selectionnez un moteur' : 'Tous les moteurs'"
        :margin="form[engineField] === null ? 'mb-3' : 'mb-2'"
    />

    <div class="col-md-12 mb-3" v-if="showEngineButtons && showEngines && form[engineField] !== null">
        <button
            type="button"
            class="btn btn-sm btn-dark bg-gradient waves-effect waves-light"
            @click.prevent="showModalEngine()"
        >
            Fiche moteur
        </button>
        <router-link
            v-if="showDetailedLink"
            :to="{ name: 'chiptuning.show', params: { id: form[engineField] } }"
            class="btn btn-sm btn-secondary bg-gradient waves-effect waves-light ms-1"
            target="_blank"
        >
            Fiche detaillee
        </router-link>
    </div>

    <Teleport to="body">
        <modal-engine-details
            v-if="showEngines"
            :id-engine="form[engineField]"
            :modal-id="modalId"
        />
    </Teleport>
</template>
